import { z } from 'zod';

/**
 * Sedi e ambito (ADR 0017): ogni dato vale per tutta l'azienda, per sedi precise o è da attribuire. Il server filtra
 * ciò che arriva al modello in base all'ambito di lettura.
 */

export const SCOPES = ['azienda', 'sedi', 'da_attribuire'] as const;
export type ScopeKind = (typeof SCOPES)[number];
export const SCOPE_SOURCES = ['motore', 'regola', 'persona'] as const;
export type ScopeSource = (typeof SCOPE_SOURCES)[number];

export const SITE_STATUSES = ['da_confermare', 'attiva', 'chiusa'] as const;
export type SiteStatus = (typeof SITE_STATUSES)[number];
/** Uso della sede (ADR 0029): operativa, solo attività interne all'azienda, solo un indirizzo (domiciliazione). */
export const SITE_USES = ['operativa', 'interna', 'domicilio'] as const;
export type SiteUse = (typeof SITE_USES)[number];

/**
 * Dove l'azienda non lavora (ADR 0029, 0051): una sede che è solo un indirizzo, o una sede interna dove la persona ha
 * detto che non lavora nessuno. Lì non ci sono impianti, reparti né posti di lavoro.
 */
export const nobodyWorks = (use: SiteUse | string | null, employees: number | null | undefined) =>
  use === 'domicilio' || (use === 'interna' && employees === 0);
/** online = pagina pubblica sulla sede, senza partita IVA (ADR 0044). */
/** «supposto»: la sceglie il cervello per l'azienda ideale, quando nessuna fonte la dice (ADR 0062). */
export const SITE_SOURCES = ['visura', 'persona', 'documento', 'posta', 'sito', 'registro', 'online', 'supposto'] as const;
export type SiteSource = (typeof SITE_SOURCES)[number];

/** Ambito come lo scrive o lo legge un client: `sedi` = id delle sedi (solo con ambito `sedi`). */
export const ScopeInputSchema = z
  .object({
    ambito: z.enum(SCOPES),
    sedi: z.array(z.string().uuid()).max(100).default([]),
  })
  .refine((s) => (s.ambito === 'sedi') === s.sedi.length > 0, { message: 'Con ambito "sedi" indica almeno una sede; altrimenti nessuna' });
export type ScopeInput = z.infer<typeof ScopeInputSchema>;

export interface Scope {
  ambito: ScopeKind;
  sedi: string[];
  /** Chi l'ha deciso: motore, regola di instradamento, persona. */
  fonte: ScopeSource;
  motivo: string | null;
}

/** Perché il titolare toglie una sede (ADR 0094): non esiste più (chiusa o trasferita) o non è dell'azienda. */
export const SITE_REMOVAL_REASONS = ['non_esiste_piu', 'non_nostra'] as const;
export type SiteRemovalReason = (typeof SITE_REMOVAL_REASONS)[number];

export const SiteSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  address: z.string().nullable(),
  municipality: z.string().nullable(),
  province: z.string().nullable(),
  legal: z.boolean(),
  /** Uso detto dalla persona; null = non ancora detto (ADR 0029). */
  use: z.enum(SITE_USES).nullable(),
  activity: z.string().nullable(),
  /** Codici ATECO dell'attività svolta nella sede (ADR 0044), come li scrive la visura per l'unità locale. */
  ateco_primary: z.string().nullable(),
  ateco_secondary: z.array(z.string()),
  /** Insegne dell'unità locale scritte nella visura (ADR 0045). */
  insegne: z.array(z.string()),
  source: z.enum(SITE_SOURCES),
  source_ref: z.string().nullable(),
  status: z.enum(SITE_STATUSES),
  /** Persone che ci lavorano (comprese quelle su tutte le sedi). */
  people: z.number().int(),
  /** Luogo e dimensione (ADR 0021): da qui dipendono le regole del posto e gli standard della sede. */
  postal_code: z.string().nullable(),
  region: z.string().nullable(),
  country: z.string().nullable(),
  /** Addetti dichiarati (visura o persona), con la data a cui si riferiscono. */
  employees: z.number().int().nullable(),
  employees_as_of: z.string().nullable(),
  /** Da dove viene il numero degli addetti (ADR 0099): una fonte delle sedi, «comune» (totale del comune nella visura) o null. */
  employees_source: z.enum([...SITE_SOURCES, 'comune']).nullable(),
  /** Il documento, la pagina o il motivo da cui viene il numero. */
  employees_ref: z.string().nullable(),
  registry_ref: z.string().nullable(),
  opened_on: z.string().nullable(),
  /** Fino a quando c'è stata (ADR 0141): passata = cessata; null = c'è ancora o non si sa. */
  closed_on: z.string().nullable(),
  /** Certa (ADR 0141): dalla visura o confermata dal titolare; le altre sono proposte. */
  certa: z.boolean(),
  /** Attività economiche che vi si svolgono. */
  activities: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  /** Le altre insegne con cui la sede è riconosciuta, oltre al nome (ADR 0070). */
  altre_insegne: z.array(z.string()),
  /** I marchi commerciali della sede trovati sulle mappe e verificati dal cervello, il principale per primo (ADR 0072). */
  marchi: z.array(z.string()),
  /** La sede è nella visura (ADR 0072): «Sede legale» o «Unità locale TA/3»; null se non c'è. */
  visura: z.string().nullable(),
  /** Gli addetti che la visura scrive per il comune della sede, con la data (ADR 0072): con più sedi nel comune, di tutte. */
  addetti_comune: z.object({ addetti: z.number().int(), al: z.string().nullable() }).nullable(),
  /** Cosa non va in questa sede, detto dal titolare a parole (ADR 0072): il cervello ne tiene conto in tutti i passi. */
  note: z.array(z.object({ testo: z.string(), quando: z.string() })),
  /** Tolta dal titolare e perché (ADR 0094); null se non l'ha tolta lui. */
  tolta: z.object({ motivo: z.enum(SITE_REMOVAL_REASONS).nullable() }).nullable(),
  /** Le altre imprese nel luogo della sede, trovate nelle fonti (ADR 0084): ogni passo ne tiene conto. */
  allo_stesso_indirizzo: z.array(z.object({ nome: z.string(), categoria: z.string().nullable(), sito: z.string().nullable(), motivo: z.string() })),
  /** Cosa è la sede, in una frase del cervello (ADR 0070). */
  sintesi: z.string().nullable(),
  /** I numeri che il titolare conosce a memoria (camere, coperti, stagione…), con la pagina che li dice (ADR 0070). */
  in_breve: z.array(z.object({ testo: z.string(), url: z.string().nullable() })),
  /** Un emoji che riconosce la sede a colpo d'occhio, scelto dal cervello (ADR 0070). */
  icona: z.string().nullable(),
});
export type Site = z.infer<typeof SiteSchema>;

const text = (max: number) => z.string().trim().min(1).max(max);

/** La foto della sede dalla sua scheda sulle mappe (ADR 0070), con chi l'ha scattata; null se non c'è. */
export interface SitePhoto {
  url: string;
  autori: Array<{ nome: string; url: string | null }>;
}

/** Una nota del titolare su una sede (ADR 0072). */
export const SiteNoteSchema = z.object({ testo: z.string().trim().min(2).max(1500) });
export type SiteNoteInput = z.infer<typeof SiteNoteSchema>;

export const CreateSiteSchema = z.object({
  name: text(120),
  address: text(300).optional(),
  municipality: text(120).optional(),
  province: text(40).optional(),
  legal: z.boolean().optional(),
  activity: text(2000).optional(),
  postal_code: text(20).optional(),
  region: text(120).optional(),
  country: text(80).optional(),
  employees: z.number().int().min(0).max(1_000_000).optional(),
  employees_as_of: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  registry_ref: text(120).optional(),
  opened_on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  closed_on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export type CreateSiteInput = z.infer<typeof CreateSiteSchema>;

export const UpdateSiteSchema = CreateSiteSchema.partial().extend({
  /** Uso della sede (ADR 0029): non operativa toglie le attività economiche di quella sede; null = non detto. */
  use: z.enum(SITE_USES).nullable().optional(),
  /** Cosa vi si svolge e riferimento nel registro si possono anche togliere (null). */
  activity: text(2000).nullable().optional(),
  registry_ref: text(120).nullable().optional(),
  /** Un indirizzo scelto tra i suggerimenti sostituisce tutti i pezzi, anche quelli che non ha (null; ADR 0028). */
  address: text(300).nullable().optional(),
  municipality: text(120).nullable().optional(),
  province: text(40).nullable().optional(),
  postal_code: text(20).nullable().optional(),
  region: text(120).nullable().optional(),
  country: text(80).nullable().optional(),
  /** Il periodo della sede si può anche togliere (null; ADR 0141). */
  opened_on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  closed_on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  /** La persona conferma una sede trovata dal motore (attiva), annulla la conferma (da_confermare) o la segna chiusa. */
  status: z.enum(SITE_STATUSES).optional(),
  /** Con status «chiusa»: perché il titolare la toglie (ADR 0094). */
  motivo: z.enum(SITE_REMOVAL_REASONS).optional(),
});
export type UpdateSiteInput = z.infer<typeof UpdateSiteSchema>;

/** Dove lavora una persona: tutte le sedi, oppure alcune (con i ruoli che vi svolge; vuoto = tutti i suoi). */
export const PersonSitesSchema = z.object({
  tutte: z.boolean(),
  sedi: z.array(z.object({ site_id: z.string().uuid(), roles: z.array(z.string()).max(50).default([]) })).max(100).default([]),
});
export type PersonSitesInput = z.infer<typeof PersonSitesSchema>;

/** Regola di instradamento della posta: i campi vuoti valgono per tutto (ma almeno uno tra casella, mittente, destinatario). */
export const SiteRoutingObjectSchema = z.object({
    mailbox_id: z.string().uuid().nullable().default(null),
    folder: text(200).nullable().default(null),
    /** Indirizzo completo o dominio (`@dominio.it`). */
    sender: z.string().trim().toLowerCase().min(3).max(254).nullable().default(null),
    recipient: z.string().trim().toLowerCase().min(3).max(254).nullable().default(null),
    ambito: z.enum(['azienda', 'sedi']),
    sedi: z.array(z.string().uuid()).max(100).default([]),
  });
export const SiteRoutingInputSchema = SiteRoutingObjectSchema
  .refine((r) => r.mailbox_id || r.sender || r.recipient, { message: 'Indica almeno la casella, il mittente o il destinatario' })
  .refine((r) => (r.ambito === 'sedi') === r.sedi.length > 0, { message: 'Con ambito "sedi" indica almeno una sede' });
export type SiteRoutingInput = z.infer<typeof SiteRoutingInputSchema>;

export interface SiteRouting {
  id: string;
  mailbox_id: string | null;
  mailbox: string | null;
  folder: string | null;
  sender: string | null;
  recipient: string | null;
  ambito: 'azienda' | 'sedi';
  sedi: string[];
  origin: 'persona' | 'proposta';
  created_at: string;
}

/** La persona attribuisce a una sede (o all'azienda) un elemento: la sua scelta non la cambia più il motore. */
export const AssignScopeObjectSchema = z.object({
    tipo: z.enum(['documento', 'messaggio', 'procedura', 'processo', 'obbligo']),
    /** id del documento (pagina Azienda), del messaggio, della procedura o del processo; chiave per un obbligo. */
    id: z.string().min(1).max(200),
    ambito: z.enum(['azienda', 'sedi']),
    sedi: z.array(z.string().uuid()).max(100).default([]),
  });
export const AssignScopeSchema = AssignScopeObjectSchema.refine((s) => (s.ambito === 'sedi') === s.sedi.length > 0, { message: 'Con ambito "sedi" indica almeno una sede' });
export type AssignScopeInput = z.infer<typeof AssignScopeSchema>;

/** Regola suggerita dal sistema: i messaggi di quel mittente in quella casella vanno quasi sempre alle stesse sedi. */
export interface SiteRoutingSuggestion {
  mailbox_id: string;
  mailbox: string;
  sender: string;
  sedi: string[];
  messaggi: number;
  totale: number;
}

/** Un indirizzo suggerito mentre si scrive (ADR 0028): `id` serve a chiederne il dettaglio. */
export interface AddressSuggestion {
  id: string;
  testo: string;
  secondario: string | null;
}

/** Suggerimenti; `disponibile` = false se il server non ha il servizio (allora l'indirizzo si scrive a mano). */
export interface AddressSuggestions {
  disponibile: boolean;
  data: AddressSuggestion[];
}

/** L'indirizzo scelto, già scomposto come lo salva una sede. */
export interface AddressDetail {
  completo: string;
  address: string | null;
  postal_code: string | null;
  municipality: string | null;
  province: string | null;
  region: string | null;
  country: string | null;
}

/**
 * Dove guardare la sede dalla strada (ADR 0028): la foto esterna di Google più vicina all'edificio e la direzione verso
 * l'edificio; `vista` null se non c'è una foto dalla strada, `punto` null se l'indirizzo non si trova.
 */
export interface SiteStreetView {
  punto: { lat: number; lng: number } | null;
  vista: { lat: number; lng: number; direzione: number } | null;
}
